import { useEffect, useState } from "react";
import type { HoldCleaningManualEntryInput } from "../models/hold-cleaning.models";
import { CLEANING_STEPS } from "./hold-cleaning-steps";
import { HoldCleaningWorkflowModal, WorkflowField, workflowInputClass } from "./HoldCleaningWorkflowModal";

type EntryKind = "overview" | "step" | "progress" | "workLog" | "outstanding" | "inventory" | "failure" | "maintenance" | "warning";

const entryKinds: Array<{ id: EntryKind; label: string; help: string }> = [
  { id: "overview", label: "Cleaning details", help: "Record the cleaning method, standard, time window or man-hours." },
  { id: "step", label: "13-step status", help: "Record one of the standard cleaning steps for a hold." },
  { id: "progress", label: "Hold progress", help: "Record the percentage and current cleaning stage for one hold." },
  { id: "workLog", label: "Work log", help: "Record cleaning work completed on a particular date." },
  { id: "outstanding", label: "Outstanding item", help: "Track an open problem or mark it resolved." },
  { id: "inventory", label: "Inventory item", help: "Record one chemical or piece of cleaning equipment." },
  { id: "failure", label: "Hold failure", help: "Record why a hold failed and any resolution." },
  { id: "maintenance", label: "Maintenance", help: "Record hold painting, repair or dry-dock work." },
  { id: "warning", label: "Chemical or equipment warning", help: "Flag a shortage, condition or other supply issue." },
];

const reportTypeFor = (kind: EntryKind): string => ({
  overview: "MANUAL_UPDATE", step: "DAILY_PROGRESS", progress: "DAILY_PROGRESS", workLog: "DAILY_PROGRESS",
  outstanding: "FAILURE_OR_WARNING", inventory: "CHEMICAL_OR_EQUIPMENT", failure: "FAILURE_OR_WARNING",
  maintenance: "MAINTENANCE", warning: "CHEMICAL_OR_EQUIPMENT",
})[kind];

const emptyEntry = (reportType: string, summary: string): HoldCleaningManualEntryInput => ({
  reportType, reportDate: null, summary, taskStatuses: [], holdProgress: [], workLog: [], outstandingItems: [],
  inventory: [], cleaningWindow: null, manHours: null, standardRequired: null, cleaningMethod: null,
  failedHolds: [], maintenanceEvents: [], chemicalOrEquipmentWarnings: [],
});

type Draft = {
  hold: string; step: string; status: "COMPLETED" | "IN_PROGRESS" | "PLANNING" | "NOT_COMPLETED" | "NOT_APPLICABLE";
  percentage: string; stage: string; stageOther: string; date: string; description: string; state: "OPEN" | "RESOLVED";
  label: string; value: string; unit: string; reason: string; resolution: string; title: string;
  detail: string; summary: string; cleaningWindow: string; manHours: string; standard: string; method: string;
};

const initialDraft = (): Draft => ({
  hold: "", step: "", status: "IN_PROGRESS", percentage: "", stage: "", stageOther: "", date: "", description: "",
  state: "OPEN", label: "", value: "", unit: "", reason: "", resolution: "", title: "", detail: "",
  summary: "", cleaningWindow: "", manHours: "", standard: "", method: "",
});

const holdLimit = (numberOfHolds?: number | null) =>
  numberOfHolds && Number.isInteger(numberOfHolds) && numberOfHolds > 0 ? Math.min(numberOfHolds, 50) : 50;

const Help = ({ text }: { text: string }) => (
  <details className="relative ml-1 inline-block align-middle">
    <summary title={text} aria-label={`Help: ${text}`} className="inline-flex h-5 w-5 cursor-pointer list-none items-center justify-center rounded-full border border-[#9CCFC8] dark:border-teal-700 text-xs font-semibold text-[#176B62] dark:text-teal-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475]">?</summary>
    <span className="absolute left-0 top-6 z-20 w-60 rounded-lg border border-[#C7D0CE] dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-xs font-normal normal-case text-[#4A5655] dark:text-gray-300 shadow-lg">{text}</span>
  </details>
);

const StepSelect = ({ value, onChange, optional = false }: { value: string; onChange: (value: string) => void; optional?: boolean }) => (
  <select value={value} onChange={(event) => onChange(event.target.value)} className={workflowInputClass}>
    <option value="">{optional ? "No specific step" : "Choose a step"}</option>
    {CLEANING_STEPS.map((step, index) => <option key={step} value={index + 1}>{index + 1}. {step}</option>)}
  </select>
);

const HoldSelect = ({ value, onChange, numberOfHolds }: { value: string; onChange: (value: string) => void; numberOfHolds?: number | null }) =>
  numberOfHolds && numberOfHolds > 0 ? (
    <select value={value} onChange={(event) => onChange(event.target.value)} className={workflowInputClass}>
      <option value="">Choose a hold</option>
      {Array.from({ length: holdLimit(numberOfHolds) }, (_, index) => <option key={index + 1} value={index + 1}>Hold {index + 1}</option>)}
    </select>
  ) : (
    <input type="number" min={1} max={50} value={value} onChange={(event) => onChange(event.target.value)} placeholder="Hold number" className={workflowInputClass} />
  );

const optionalHoldNumbers = (text: string, selected: number[], numberOfHolds?: number | null): number[] | null => {
  if (numberOfHolds && numberOfHolds > 0) return selected;
  if (!text.trim()) return [];
  const values = text.split(",").map((item) => Number(item.trim()));
  return values.every((value) => Number.isInteger(value) && value >= 1 && value <= 50)
    ? Array.from(new Set(values)) : null;
};

export const HoldCleaningManualEntryModal = ({
  open, numberOfHolds, onClose, onSave,
}: {
  open: boolean;
  numberOfHolds: number | null | undefined;
  onClose: () => void;
  onSave: (input: HoldCleaningManualEntryInput) => Promise<void>;
}) => {
  const [kind, setKind] = useState<EntryKind | null>(null);
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [selectedHolds, setSelectedHolds] = useState<number[]>([]);
  const [holdText, setHoldText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind(null); setDraft(initialDraft()); setSelectedHolds([]); setHoldText(""); setError(null);
  }, [open]);

  const change = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const knownHold = (): number | null => {
    const hold = Number(draft.hold);
    return Number.isInteger(hold) && hold >= 1 && hold <= holdLimit(numberOfHolds) ? hold : null;
  };
  const requireHold = (): number | null => {
    const hold = knownHold();
    if (hold === null) setError(`Choose a hold from 1 to ${holdLimit(numberOfHolds)}.`);
    return hold;
  };
  const holds = () => optionalHoldNumbers(holdText, selectedHolds, numberOfHolds);
  const isoDate = (value: string): string | null => {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? null : date.toISOString();
  };

  const buildEntry = (): HoldCleaningManualEntryInput | null => {
    if (!kind) { setError("Choose what you want to record."); return null; }
    const input = emptyEntry(reportTypeFor(kind), draft.summary.trim() || entryKinds.find((item) => item.id === kind)!.label);
    const holdNumbers = holds();
    if (holdNumbers === null) { setError("Enter valid hold numbers between 1 and 50."); return null; }
    if (kind === "overview") {
      const hours = draft.manHours.trim() ? Number(draft.manHours) : null;
      if (hours !== null && (!Number.isFinite(hours) || hours < 0)) { setError("Man-hours must be zero or greater."); return null; }
      if (![draft.cleaningWindow, draft.manHours, draft.standard, draft.method].some((value) => value.trim())) {
        setError("Enter at least one cleaning detail."); return null;
      }
      input.cleaningWindow = draft.cleaningWindow.trim() || null;
      input.manHours = hours;
      input.standardRequired = draft.standard.trim() || null;
      input.cleaningMethod = draft.method.trim() || null;
      if (draft.date) {
        input.reportDate = isoDate(draft.date);
        if (!input.reportDate) { setError("Choose a valid report date."); return null; }
      }
    } else if (kind === "step") {
      const hold = requireHold();
      if (hold === null) return null;
      const step = Number(draft.step);
      if (!Number.isInteger(step) || step < 1 || step > 13) { setError("Choose a cleaning step."); return null; }
      input.taskStatuses = [{ holdNumber: hold, stepNumber: step, status: draft.status }];
      input.summary = `Hold ${hold}: ${CLEANING_STEPS[step - 1]} — ${draft.status.toLowerCase().replaceAll("_", " ")}`;
    } else if (kind === "progress") {
      const hold = requireHold();
      if (hold === null) return null;
      const percentage = Number(draft.percentage);
      const stage = draft.stage === "Other" ? draft.stageOther.trim() : draft.stage;
      if (!draft.percentage || !Number.isFinite(percentage) || percentage < 0 || percentage > 100 || !stage) {
        setError("Enter a percentage from 0 to 100 and choose the current stage."); return null;
      }
      input.holdProgress = [{ holdNumber: hold, percentage, stage }];
      input.summary = `Hold ${hold}: ${percentage}% complete`;
    } else if (kind === "workLog") {
      const date = isoDate(draft.date);
      if (!date || !draft.description.trim()) { setError("Enter a work date and description."); return null; }
      input.workLog = [{ occurredAt: date, description: draft.description.trim(), holdNumbers }];
      input.summary = draft.description.trim();
    } else if (kind === "outstanding") {
      if (!draft.description.trim()) { setError("Describe the item."); return null; }
      input.outstandingItems = [{ description: draft.description.trim(), holdNumbers, stepNumber: draft.step ? Number(draft.step) : null, state: draft.state }];
      input.summary = draft.description.trim();
    } else if (kind === "inventory") {
      if (!draft.label.trim() || !draft.value.trim()) { setError("Enter an item name and value."); return null; }
      input.inventory = [{ label: draft.label.trim(), value: draft.value.trim(), unit: draft.unit.trim() || null }];
      input.summary = `${draft.label.trim()}: ${draft.value.trim()}`;
    } else if (kind === "failure") {
      const hold = requireHold();
      if (hold === null) return null;
      const stage = draft.stage === "Other" ? draft.stageOther.trim() : draft.stage;
      if (!stage || !draft.reason.trim()) { setError("Choose a stage and describe the failure."); return null; }
      input.failedHolds = [{ holdNumber: hold, stage, reason: draft.reason.trim(), resolution: draft.resolution.trim() || null, state: draft.state }];
      input.summary = `Hold ${hold}: ${draft.reason.trim()}`;
    } else if (kind === "maintenance") {
      if (!draft.title.trim() || !draft.detail.trim()) { setError("Enter a title and details."); return null; }
      const date = draft.date ? isoDate(draft.date) : null;
      if (draft.date && !date) { setError("Choose a valid maintenance date."); return null; }
      input.maintenanceEvents = [{ title: draft.title.trim(), detail: draft.detail.trim(), occurredAt: date }];
      input.summary = draft.title.trim();
    } else {
      if (!draft.description.trim()) { setError("Describe the warning."); return null; }
      input.chemicalOrEquipmentWarnings = [{ description: draft.description.trim(), state: draft.state }];
      input.summary = draft.description.trim();
    }
    return input;
  };

  const submit = async () => {
    const input = buildEntry();
    if (!input) return;
    setBusy(true); setError(null);
    try { await onSave(input); onClose(); }
    catch (requestError) { setError(requestError instanceof Error ? requestError.message : "The entry could not be saved."); }
    finally { setBusy(false); }
  };

  const selected = entryKinds.find((item) => item.id === kind);
  const selectKind = (next: EntryKind) => { setKind(next); setDraft(initialDraft()); setSelectedHolds([]); setHoldText(""); setError(null); };
  const multiHolds = (
    <fieldset>
      <legend className="text-xs font-semibold uppercase tracking-wide text-[#4A5655] dark:text-gray-300">Holds (optional)</legend>
      {numberOfHolds && numberOfHolds > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {Array.from({ length: holdLimit(numberOfHolds) }, (_, index) => index + 1).map((hold) => (
            <label key={hold} className="flex items-center gap-1 rounded-md border border-[#C7D0CE] dark:border-gray-600 px-2 py-1 text-xs normal-case">
              <input type="checkbox" checked={selectedHolds.includes(hold)} onChange={(event) => setSelectedHolds((current) => event.target.checked ? [...current, hold].sort((a, b) => a - b) : current.filter((value) => value !== hold))} /> Hold {hold}
            </label>
          ))}
        </div>
      ) : <input aria-label="Hold numbers" value={holdText} onChange={(event) => setHoldText(event.target.value)} placeholder="e.g. 1, 2" className={workflowInputClass} />}
    </fieldset>
  );

  return (
    <HoldCleaningWorkflowModal open={open} title="New cleaning entry" description="Choose one update to record. It will be added to this voyage." busy={busy} error={error} submitLabel="Save entry" onClose={onClose} onSubmit={submit}>
      <div className="flex items-center gap-1 text-sm font-semibold">What would you like to record?<Help text="Choose one kind of update. You can add another entry afterwards." /></div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {entryKinds.map((item) => (
          <button key={item.id} type="button" onClick={() => selectKind(item.id)} aria-pressed={kind === item.id} className={`rounded-lg border px-3 py-3 text-left text-sm font-semibold ${kind === item.id ? "border-[#1F8475] bg-[#E9F4F3] dark:bg-teal-950 text-[#176B62] dark:text-teal-200" : "border-[#DFE4E2] dark:border-gray-700 hover:bg-[#F8F9F9] dark:hover:bg-gray-900"}`}>{item.label}</button>
        ))}
      </div>
      {selected && <section className="mt-5 rounded-lg border border-[#DFE4E2] dark:border-gray-700 p-4">
        <h3 className="flex items-center text-base font-semibold">{selected.label}<Help text={selected.help} /></h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {kind === "overview" && <>
            <WorkflowField label="Report date (optional)"><input type="datetime-local" value={draft.date} onChange={(event) => change("date", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Cleaning window"><input value={draft.cleaningWindow} onChange={(event) => change("cleaningWindow", event.target.value)} placeholder="e.g. 3 sea days" className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Man-hours"><input type="number" min={0} value={draft.manHours} onChange={(event) => change("manHours", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Required standard"><input value={draft.standard} onChange={(event) => change("standard", event.target.value)} placeholder="e.g. grain clean" className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Cleaning method"><input value={draft.method} onChange={(event) => change("method", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Summary (optional)"><input value={draft.summary} onChange={(event) => change("summary", event.target.value)} className={workflowInputClass} /></WorkflowField>
          </>}
          {kind === "step" && <>
            <WorkflowField label="Hold"><HoldSelect value={draft.hold} onChange={(value) => change("hold", value)} numberOfHolds={numberOfHolds} /></WorkflowField>
            <WorkflowField label="Cleaning step"><StepSelect value={draft.step} onChange={(value) => change("step", value)} /></WorkflowField>
            <WorkflowField label="Status"><select value={draft.status} onChange={(event) => change("status", event.target.value as Draft["status"])} className={workflowInputClass}><option value="PLANNING">Planned</option><option value="IN_PROGRESS">In progress</option><option value="COMPLETED">Completed</option><option value="NOT_COMPLETED">Not completed</option><option value="NOT_APPLICABLE">Not applicable</option></select></WorkflowField>
          </>}
          {kind === "progress" && <>
            <WorkflowField label="Hold"><HoldSelect value={draft.hold} onChange={(value) => change("hold", value)} numberOfHolds={numberOfHolds} /></WorkflowField>
            <WorkflowField label="Progress (%)"><input type="number" min={0} max={100} value={draft.percentage} onChange={(event) => change("percentage", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Current stage"><select value={draft.stage} onChange={(event) => change("stage", event.target.value)} className={workflowInputClass}><option value="">Choose a stage</option>{CLEANING_STEPS.map((step, index) => <option key={step} value={step}>{index + 1}. {step}</option>)}<option value="Other">Other</option></select>{draft.stage === "Other" && <input placeholder="Describe the stage" value={draft.stageOther} onChange={(event) => change("stageOther", event.target.value)} className={workflowInputClass} />}</WorkflowField>
          </>}
          {kind === "workLog" && <>
            <WorkflowField label="Work date"><input type="datetime-local" value={draft.date} onChange={(event) => change("date", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Description"><input value={draft.description} onChange={(event) => change("description", event.target.value)} className={workflowInputClass} /></WorkflowField>
            {multiHolds}
          </>}
          {kind === "outstanding" && <>
            <WorkflowField label="Description"><input value={draft.description} onChange={(event) => change("description", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Cleaning step (optional)"><StepSelect value={draft.step} onChange={(value) => change("step", value)} optional /></WorkflowField>
            <WorkflowField label="State"><select value={draft.state} onChange={(event) => change("state", event.target.value as Draft["state"])} className={workflowInputClass}><option value="OPEN">Open</option><option value="RESOLVED">Resolved</option></select></WorkflowField>
            {multiHolds}
          </>}
          {kind === "inventory" && <>
            <WorkflowField label="Item"><input value={draft.label} onChange={(event) => change("label", event.target.value)} placeholder="Chemical or equipment" className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Quantity or value"><input value={draft.value} onChange={(event) => change("value", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Unit (optional)"><input list="hold-cleaning-units" value={draft.unit} onChange={(event) => change("unit", event.target.value)} className={workflowInputClass} /><datalist id="hold-cleaning-units"><option value="l" /><option value="kg" /><option value="mt" /><option value="units" /></datalist></WorkflowField>
          </>}
          {kind === "failure" && <>
            <WorkflowField label="Hold"><HoldSelect value={draft.hold} onChange={(value) => change("hold", value)} numberOfHolds={numberOfHolds} /></WorkflowField>
            <WorkflowField label="Cleaning stage"><select value={draft.stage} onChange={(event) => change("stage", event.target.value)} className={workflowInputClass}><option value="">Choose a stage</option>{CLEANING_STEPS.map((step, index) => <option key={step} value={step}>{index + 1}. {step}</option>)}<option value="Other">Other</option></select>{draft.stage === "Other" && <input placeholder="Describe the stage" value={draft.stageOther} onChange={(event) => change("stageOther", event.target.value)} className={workflowInputClass} />}</WorkflowField>
            <WorkflowField label="Reason"><input value={draft.reason} onChange={(event) => change("reason", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Resolution (optional)"><input value={draft.resolution} onChange={(event) => change("resolution", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="State"><select value={draft.state} onChange={(event) => change("state", event.target.value as Draft["state"])} className={workflowInputClass}><option value="OPEN">Open</option><option value="RESOLVED">Resolved</option></select></WorkflowField>
          </>}
          {kind === "maintenance" && <>
            <WorkflowField label="Title"><input value={draft.title} onChange={(event) => change("title", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="Date (optional)"><input type="datetime-local" value={draft.date} onChange={(event) => change("date", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <div className="sm:col-span-2"><WorkflowField label="Details"><textarea rows={3} value={draft.detail} onChange={(event) => change("detail", event.target.value)} className={workflowInputClass} /></WorkflowField></div>
          </>}
          {kind === "warning" && <>
            <WorkflowField label="Warning"><input value={draft.description} onChange={(event) => change("description", event.target.value)} className={workflowInputClass} /></WorkflowField>
            <WorkflowField label="State"><select value={draft.state} onChange={(event) => change("state", event.target.value as Draft["state"])} className={workflowInputClass}><option value="OPEN">Open</option><option value="RESOLVED">Resolved</option></select></WorkflowField>
          </>}
        </div>
      </section>}
    </HoldCleaningWorkflowModal>
  );
};
